// TemplateEditor — design-system component for rich-text template editing.
//
// Intentionally exposed via a thin abstraction so the underlying editor
// engine can be swapped without touching callers. Today this wraps
// CKEditor 5's React binding; tomorrow it can be swapped for TipTap,
// Lexical, BlockNote, or anything else without changing the call site.
//
// Output format: HTML string. Every modern editor round-trips HTML
// cleanly, so this format is the portability layer.
//
// Licensing: CKEditor 5 ships under GPL 2.0+. SaaS use does NOT
// trigger distribution-side GPL obligations; the source-disclosure
// clause only kicks in on binary redistribution. We can swap to a
// commercial license OR a different editor when we want premium
// features or drop the "Powered by CKEditor" attribution.
//
// Bundle cost: CKEditor 5 is ~600KB minified. The component is split
// into a thin wrapper + a lazy-loaded heavy chunk so non-editor routes
// don't pay the cost. Vite's code-splitting handles the heavy lifting.
import { Suspense, lazy } from "react";
// [S-715] Type-only (erased at build) — see draftsQuery.ts for the same
// pattern. TemplateEditor is engine-agnostic per the file header above; the
// CKEditor-specific rendering of these lives entirely in
// TemplateEditor.ckeditor.tsx, keeping this contract portable to a future
// editor swap.
import type { DraftCitation } from "@csai/core";
export type { DraftCitation };

export type TemplateEditorProps = {
  /** Current HTML content of the editor. Controlled by the caller. */
  value: string;
  /** Fires on every edit. Caller decides whether to debounce. */
  onChange: (next: string) => void;
  /** When true, the editor renders read-only (preview mode). */
  readOnly?: boolean;
  /** Placeholder shown when value is empty. */
  placeholder?: string;
  /** Optional ARIA label for the editing surface. */
  ariaLabel?: string;
  /**
   * Fires once the editor is initialized, with the editor instance. Callers
   * that need the editor's model APIs (e.g. find-and-scroll to a clause) use
   * this rather than scraping the rendered DOM. Typed loosely so callers don't
   * couple to the CKEditor build.
   */
  onReady?: (editor: unknown) => void;
  /**
   * [S-715/E-DRAFT-CITATIONS] Per-value provenance for `value`, from
   * DraftGeneratorPort (S-713) — read once at mount, same as `value` itself
   * (callers already remount on content swap; see EditorFrame's `key` usage
   * in DraftDetail.tsx). Absent/empty renders no citation marks.
   */
  citations?: DraftCitation[];
  /** Fires when the user clicks a citation mark while citations are toggled
   *  visible. Absent citations/onOpenCitation → the feature is simply inert
   *  (no marks render), never a dead click. */
  onOpenCitation?: (citation: DraftCitation) => void;
  /** [rail epic] The toolbar (~45 items) is tuned for full-page width by
   *  forcing `shouldNotGroupWhenFull: true` — every item stays on one row,
   *  wrapping instead of overflowing. At rail width (~420px) that wraps to
   *  several rows and eats the editor's vertical space. `compact` re-enables
   *  CKEditor's own responsive overflow ("»" dropdown for items that don't
   *  fit) instead of a hand-trimmed toolbar. */
  compact?: boolean;
};

// Lazy chunk — only loaded when the editor is mounted. The
// implementation file imports the heavy CKEditor packages at module
// load, so this dynamic import is what splits the bundle.
const CkEditorImpl = lazy(() =>
  import("./TemplateEditor.ckeditor").then((m) => ({ default: m.CkEditorImpl }))
);

/**
 * TemplateEditor — rich-text editor exposed via a clean prop interface.
 *
 * The wrapping name (`TemplateEditor`) deliberately does NOT mention
 * CKEditor — callers shouldn't depend on the engine. To swap to TipTap /
 * Lexical / BlockNote later, replace `TemplateEditor.ckeditor.tsx` with
 * the alternative's mounting code and keep this signature stable.
 */
export function TemplateEditor(props: TemplateEditorProps): JSX.Element {
  return (
    <div className="tpl-editor" aria-label={props.ariaLabel}>
      <Suspense fallback={<EditorSkeleton />}>
        <CkEditorImpl {...props} />
      </Suspense>
    </div>
  );
}

function EditorSkeleton(): JSX.Element {
  return (
    <div className="tpl-editor__skeleton" aria-busy="true">
      Loading editor…
    </div>
  );
}
